import { useState } from 'react'
import { CalendarClock, KeyRound, Pencil, Play, Plus, Trash2 } from 'lucide-react'
import { ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useExportConfigs, useExportMutations, useExportReports } from '../api'
import { CRON_PRESETS, DESTINATION_LABELS, DESTINATION_NOTES } from '../types'
import type { ExportConfigView, ExportDestination, ExportFormat, RunView } from '../types'
import { Select } from '@/components/ui/select'

interface LegalEntityOption { id: string; name: string }

interface Draft {
  id?: string
  legalEntityId: string
  name: string
  reportKey: string
  format: ExportFormat
  destination: ExportDestination
  targetPath: string
  sftpHost: string
  sftpPort: string
  sftpUsername: string
  sftpPassword: string
  sftpHostKey: string
  cron: string
  active: boolean
  passwordStored: boolean
}

const EMPTY: Draft = {
  legalEntityId: '',
  name: '',
  reportKey: '',
  format: 'XLSX',
  destination: 'LOCAL',
  targetPath: '',
  sftpHost: '',
  sftpPort: '22',
  sftpUsername: '',
  sftpPassword: '',
  sftpHostKey: '',
  cron: '0 30 2 * * ?',
  active: true,
  passwordStored: false,
}

function toDraft(config: ExportConfigView): Draft {
  return {
    id: config.id,
    legalEntityId: config.legalEntityId ?? '',
    name: config.name,
    reportKey: config.reportKey,
    format: config.format,
    destination: config.destination,
    targetPath: config.targetPath,
    sftpHost: config.sftpHost ?? '',
    sftpPort: config.sftpPort == null ? '22' : String(config.sftpPort),
    sftpUsername: config.sftpUsername ?? '',
    sftpPassword: '',
    sftpHostKey: config.sftpHostKey ?? '',
    cron: config.cron,
    active: config.active,
    passwordStored: config.sftpPasswordSet,
  }
}

/**
 * Spreadsheets that leave on a schedule.
 *
 * <p>Last run and its message are the column that matters. An export nobody can see the state
 * of stops working the week somebody rotates the SFTP password and is noticed the quarter
 * after, so a failure is on the row rather than only in a log.
 */
export function ReportExportsPage() {
  useBreadcrumb()
  const configs = useExportConfigs()
  const reports = useExportReports()
  const entities = useLookup<LegalEntityOption>('legalEntities')
  const { create, update, remove, runNow } = useExportMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toRemove, setToRemove] = useState<ExportConfigView | null>(null)
  const [lastRun, setLastRun] = useState<RunView | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onRunNow(id: string) {
    setLastRun(null)
    try {
      setLastRun(await runNow.mutateAsync(id))
    } catch (caught) {
      setLastRun({
        status: 'FAILED',
        message: toApiProblem(caught)?.detail ?? 'The export could not be run',
        rowCount: 0,
        bytes: 0,
      })
    }
  }

  async function onSave() {
    if (!draft) return
    setError(null)
    const body = {
      legalEntityId: draft.legalEntityId || null,
      name: draft.name,
      reportKey: draft.reportKey,
      format: draft.format,
      destination: draft.destination,
      targetPath: draft.targetPath,
      sftpHost: draft.sftpHost || null,
      sftpPort: draft.sftpPort ? Number(draft.sftpPort) : null,
      sftpUsername: draft.sftpUsername || null,
      // Sent only when typed: the stored one never reaches this screen, so echoing an
      // empty string back would clear it on every save.
      ...(draft.sftpPassword ? { sftpPassword: draft.sftpPassword } : {}),
      sftpHostKey: draft.sftpHostKey || null,
      cron: draft.cron,
      active: draft.active,
    }
    try {
      if (draft.id) await update.mutateAsync({ id: draft.id, body })
      else await create.mutateAsync(body)
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That export could not be saved')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Scheduled exports"
        subtitle="A report, written to a folder or an SFTP server, on a schedule."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            New export
          </Button>
        }
      />

      {lastRun && (
        <Card
          role="status"
          className={`p-4 text-sm ${lastRun.status === 'OK'
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
            : 'border-red-300 bg-red-50 text-red-900'}`}
        >
          <p className="font-medium">{lastRun.status === 'OK' ? 'Delivered' : 'Failed'}</p>
          <p>{lastRun.message}</p>
        </Card>
      )}

      <Card>
        {configs.isPending && <Skeleton className="m-4 h-24" />}
        {configs.isError && <ErrorState error={configs.error} onRetry={() => void configs.refetch()} />}
        {configs.isSuccess && configs.data.length === 0 && (
          <EmptyState
            title="Nothing is scheduled"
            description="Pick a report, a destination and a time, and it will be delivered without anybody logging in."
          />
        )}
        {configs.isSuccess && configs.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Report</th>
                  <th className="p-3 font-medium">Goes to</th>
                  <th className="p-3 font-medium">When</th>
                  <th className="p-3 font-medium">Last run</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {configs.data.map((config) => (
                  <tr key={config.id} className="border-b border-slate-100">
                    <td className="p-3">
                      {config.name}
                      {!config.active && <span className="ms-2 text-slate-500">(paused)</span>}
                    </td>
                    <td className="p-3 text-slate-600">
                      {reports.data?.find((report) => report.key === config.reportKey)?.label
                        ?? config.reportKey}
                      <span className="ms-1 text-slate-500">· {config.format}</span>
                    </td>
                    <td className="p-3 text-slate-600">
                      {DESTINATION_LABELS[config.destination]}
                      <span className="block font-mono text-xs">{config.targetPath}</span>
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-600">{config.cron}</td>
                    <td className="p-3">
                      {config.lastRunStatus == null ? (
                        <span className="text-slate-500">never</span>
                      ) : (
                        <>
                          <StatusBadge
                            status={config.lastRunStatus}
                            tone={config.lastRunStatus === 'OK' ? 'success' : 'danger'}
                            label={config.lastRunStatus === 'OK' ? 'OK' : 'Failed'}
                          />
                          <span className="mt-0.5 block max-w-xs truncate text-xs text-slate-600" title={config.lastRunMessage ?? ''}>
                            {config.lastRunMessage}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={runNow.isPending}
                          onClick={() => void onRunNow(config.id)}
                        >
                          <Play className="size-4" aria-hidden="true" />
                          Run now
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setError(null); setDraft(toDraft(config)) }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                          Edit
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => setToRemove(config)}>
                          <Trash2 className="size-4" aria-hidden="true" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title={draft?.id ? 'Edit this export' : 'New scheduled export'}
        description="A report, a destination and a schedule. Nothing runs until this is saved and active."
        submitLabel={draft?.id ? 'Save changes' : 'Create it'}
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void onSave()}
      >
        {draft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="export-name">Name</Label>
                <Input
                  id="export-name"
                  value={draft.name}
                  placeholder="Nightly headcount for finance"
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="export-report">Report</Label>
                <Select
                  id="export-report"
                  className="w-full"
                  value={draft.reportKey}
                  onChange={(event) => setDraft({ ...draft, reportKey: event.target.value })}
                >
                  <option value="">Choose a report…</option>
                  {reports.data?.map((report) => (
                    <option key={report.key} value={report.key}>{report.label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="export-format">Format</Label>
                <Select
                  id="export-format"
                  className="w-full"
                  value={draft.format}
                  onChange={(event) => setDraft({ ...draft, format: event.target.value as ExportFormat })}
                >
                  <option value="XLSX">Excel workbook (.xlsx)</option>
                  <option value="CSV">CSV</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="export-entity">Limit to one entity</Label>
                <Select
                  id="export-entity"
                  className="w-full"
                  value={draft.legalEntityId}
                  onChange={(event) => setDraft({ ...draft, legalEntityId: event.target.value })}
                >
                  <option value="">Every entity in the company</option>
                  {entities.data?.map((entity) => (
                    <option key={entity.id} value={entity.id}>{entity.name}</option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="export-destination">Destination</Label>
              <Select
                id="export-destination"
                className="w-full"
                value={draft.destination}
                onChange={(event) =>
                  setDraft({ ...draft, destination: event.target.value as ExportDestination })}
              >
                {Object.entries(DESTINATION_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <p className="mt-1 text-sm text-slate-600">{DESTINATION_NOTES[draft.destination]}</p>
            </div>

            <div>
              <Label htmlFor="export-path">
                {draft.destination === 'SFTP' ? 'Directory on the server' : 'Folder'}
              </Label>
              <Input
                id="export-path"
                value={draft.targetPath}
                placeholder={draft.destination === 'SFTP' ? '/drop/hrms' : '/var/lib/smart-hrms/exports'}
                onChange={(event) => setDraft({ ...draft, targetPath: event.target.value })}
              />
            </div>

            {draft.destination === 'SFTP' && (
              <div className="space-y-3 rounded-md border border-slate-200 p-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <Label htmlFor="sftp-host">Host</Label>
                    <Input
                      id="sftp-host"
                      value={draft.sftpHost}
                      onChange={(event) => setDraft({ ...draft, sftpHost: event.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="sftp-port">Port</Label>
                    <Input
                      id="sftp-port"
                      type="number"
                      value={draft.sftpPort}
                      onChange={(event) => setDraft({ ...draft, sftpPort: event.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="sftp-user">Username</Label>
                    <Input
                      id="sftp-user"
                      value={draft.sftpUsername}
                      onChange={(event) => setDraft({ ...draft, sftpUsername: event.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="sftp-password">
                      Password
                      {draft.passwordStored && (
                        <span className="ms-2 inline-flex items-center gap-1 text-sm font-normal text-emerald-700">
                          <KeyRound className="size-3.5" aria-hidden="true" /> one is stored
                        </span>
                      )}
                    </Label>
                    <Input
                      id="sftp-password"
                      type="password"
                      autoComplete="off"
                      value={draft.sftpPassword}
                      placeholder={draft.passwordStored ? 'Leave blank to keep it' : ''}
                      onChange={(event) => setDraft({ ...draft, sftpPassword: event.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="sftp-host-key">Host key fingerprint</Label>
                  <Input
                    id="sftp-host-key"
                    value={draft.sftpHostKey}
                    placeholder="SHA256:…"
                    onChange={(event) => setDraft({ ...draft, sftpHostKey: event.target.value })}
                  />
                  <p className="mt-1 text-sm text-slate-600">
                    Required. Without it the run fails rather than trusting whoever answers on
                    that port — this file is a whole company&rsquo;s data. Read it with{' '}
                    <code className="text-xs">
                      ssh-keyscan -t rsa,ed25519 {draft.sftpHost || 'host'} | ssh-keygen -lf -
                    </code>
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="export-cron">Schedule</Label>
              <div className="flex gap-2">
                <Input
                  id="export-cron"
                  className="font-mono"
                  value={draft.cron}
                  onChange={(event) => setDraft({ ...draft, cron: event.target.value })}
                />
                <Select
                  aria-label="Common schedules"
                  value=""
                  onChange={(event) => {
                    if (event.target.value) setDraft({ ...draft, cron: event.target.value })
                  }}
                >
                  <option value="">Pick a common one…</option>
                  {CRON_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                  ))}
                </Select>
              </div>
              <p className="mt-1 flex items-start gap-1.5 text-sm text-slate-600">
                <CalendarClock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                Quartz cron, not Unix cron: seconds come first, and one of day-of-month or
                day-of-week must be <code className="mx-1">?</code>.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4"
                checked={draft.active}
                onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
              />
              Run on this schedule
            </label>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toRemove != null}
        onOpenChange={(open) => { if (!open) setToRemove(null) }}
        title="Delete this export?"
        description={toRemove ? `"${toRemove.name}" stops running. Files already delivered are not touched.` : ''}
        confirmLabel="Delete"
        onConfirm={() => {
          if (toRemove) remove.mutate(toRemove.id)
          setToRemove(null)
        }}
      />
    </div>
  )
}
